/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { Stack } from "@fluentui/react";

import * as distributionListsActions from "Dataflows/DistributionLists/DistributionListsAction";

import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import DistributionLists from "Components/DistributionLists/DistributionLists";
import { OperationStatus } from "Constants/OperationStatus";
import { SubSiteUrlRootPath } from "Constants/TenantTypes";
import {
  containerStackStyles,
  listStackStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import {
  getCurrentPage,
  getDistributionListsByTenant,
  getDistributionListsByTenantCount,
  getFetchDistributionListsByTenantError,
  getNetworkOperationStatusFetchByTenant,
  getPageSize,
} from "Dataflows/DistributionLists/DistributionListsSelector";

import { IDistributionListsContainerMatchProps } from "./IDistributionListRequestsContainerProps";

const DistributionListsContainer = () => {
  const dispatch = useDispatch();
  const routeParams = useParams<IDistributionListsContainerMatchProps>(); // TODO: Update interface
  const tenant = SubSiteUrlRootPath[routeParams.tenant];
  const distributionLists = useSelector(getDistributionListsByTenant);
  const distributionListCount = useSelector(getDistributionListsByTenantCount);
  const networkOperationStatusFetchByTenant = useSelector(getNetworkOperationStatusFetchByTenant);
  const fetchDistributionListsByTenantError = useSelector(getFetchDistributionListsByTenantError);
  const pageSize = useSelector(getPageSize);
  const currentPage = useSelector(getCurrentPage);

  useEffect(() => {
    dispatch(
      distributionListsActions.fetchDistributionListsByTenant(tenant, pageSize, currentPage)
    );
  }, [tenant, currentPage, pageSize]);

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>Distribution Lists</Title>
            <ListCounts
              total={distributionListCount}
              loading={networkOperationStatusFetchByTenant === OperationStatus.Pending}
            />
          </PaddedFlexRow>
          <DistributionLists
            data={distributionLists}
            tenant={tenant}
            filteredCount={distributionListCount}
            pageSize={pageSize}
            isLoadingDLs={networkOperationStatusFetchByTenant === OperationStatus.Pending}
            currentPage={currentPage}
            loadDLsError={fetchDistributionListsByTenantError}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

export default DistributionListsContainer;
